import React, { useState } from 'react';
import SEO from '../components/SEO';
import { Search, ChevronDown, Plus, ArrowLeft, Save } from 'lucide-react';

const DocumentTemplate = ({ title, type, color, imageUrl }) => {
  // Transform color names to tailwind color classes
  const getColorClass = (colorName) => {
    const colorMap = {
      'Serif': 'indigo',
      'Coral': 'rose',
      'Spearmint': 'emerald',
      'Tropic': 'cyan',
      'Geometric': 'amber',
      'Luxe': 'purple',
      'gray': 'gray'
    };
    
    return colorMap[colorName] || 'blue';
  };
  
  const bgColorClass = `bg-${getColorClass(color)}-50`;
  const textColorClass = `text-${getColorClass(color)}-600`;
  
  return (
    <div className="flex flex-col items-center mx-3 group">
      <div className="w-40 h-56 border rounded-lg overflow-hidden hover:border-blue-500 hover:shadow-lg cursor-pointer transition-all duration-200">
        {imageUrl ? (
          <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
        ) : (
          <div className={`w-full h-full ${bgColorClass} flex items-center justify-center transition-colors duration-200`}>
            {type === 'blank' && (
              <div className="text-5xl text-yellow-500 font-bold group-hover:scale-110 transition-transform duration-200">+</div>
            )}
            {type !== 'blank' && (
              <div className={`text-base ${textColorClass} p-3 font-medium`}>{title}</div>
            )}
          </div>
        )}
      </div>
      <p className="mt-3 text-sm font-medium">{title}</p>
      {type !== 'blank' && <p className="text-xs text-gray-500">{color}</p>}
    </div>
  );
};

const DocumentEditor = ({ template, onClose }) => {
  return (
    <div className="w-full h-full flex flex-col">
      <div className="bg-white border-b p-3 flex items-center space-x-4 shadow-sm">
        <button 
          onClick={onClose}
          className="px-3 py-1.5 text-sm rounded-md hover:bg-gray-100 flex items-center gap-1 transition-colors"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <input 
          type="text" 
          placeholder="Untitled document" 
          className="px-3 py-1.5 border border-gray-300 rounded-md flex-grow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        />
        <button className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1">
          <Save size={16} /> Save
        </button>
      </div>
      <div className="p-4 flex-grow bg-gray-100">
        <div className="mx-auto max-w-3xl min-h-64 bg-white shadow-md p-8 border rounded-md">
          <p className="text-gray-400">Start typing your {template.toLowerCase()} content here...</p>
        </div>
      </div>
    </div>
  );
};

const ProducerBuddyDocs = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recentDocuments, setRecentDocuments] = useState([]);

  const templates = [
    { id: 1, title: 'Blank document', type: 'blank', color: 'gray' },
    { id: 2, title: 'Resume', type: 'template', color: 'Serif' },
    { id: 3, title: 'Resume', type: 'template', color: 'Coral' },
    { id: 4, title: 'Letter', type: 'template', color: 'Spearmint' },
    { id: 5, title: 'Project proposal', type: 'template', color: 'Tropic' },
    { id: 6, title: 'Brochure', type: 'template', color: 'Geometric' },
    { id: 7, title: 'Report', type: 'template', color: 'Luxe' },
  ];

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
  };

  const handleCloseEditor = () => {
    setSelectedTemplate(null);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <SEO
        title="Documentation Tool"
        description="Create, manage, and organise film production documents and templates with ProducerBuddy's documentation tool."
        url="https://producerbuddy.pilomation.com/documentation"
        robots="noindex, nofollow"
      />
      {/* Header with app name and search */}
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b">
        <div className="flex items-center">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="#4285F4" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" />
          </svg>
          <span className="ml-3 font-semibold text-xl text-gray-800">Producer Buddy Docs</span>
        </div>
        
        {/* Search bar */}
        <div className="flex flex-grow max-w-2xl mx-8 bg-gray-100 rounded-xl px-4 py-2.5 hover:bg-gray-200 focus-within:bg-white focus-within:shadow-md transition-all duration-200 border border-transparent focus-within:border-gray-300">
          <Search className="text-gray-500 mr-2" size={18} />
          <input
            type="text"
            placeholder="Search documents"
            className="bg-transparent border-none outline-none flex-grow text-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="flex items-center space-x-4">
          <button className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
            <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-lg overflow-hidden">
              PB
            </div>
          </button>
        </div>
      </div>

      {/* Main content */}
      {!selectedTemplate ? (
        <div className="flex-grow overflow-auto">
          <div className="max-w-6xl mx-auto py-8 px-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-800">Start a new document</h2>
              <div className="flex items-center">
                <button className="text-sm text-gray-600 hover:text-blue-600 transition-colors flex items-center gap-1 px-3 py-1.5 rounded-md hover:bg-white">
                  Template gallery <ChevronDown size={16} />
                </button>
                <button className="ml-2 p-2 text-gray-600 hover:text-blue-600 hover:bg-white rounded-md transition-colors">
                  <Plus size={18} />
                </button>
              </div>
            </div>

            {/* Template gallery with increased height */}
            <div className="overflow-x-auto pb-8 -mx-2 px-2">
              <div className="flex flex-nowrap min-w-max">
                {templates.map((template) => (
                  <div key={template.id} onClick={() => handleSelectTemplate(template.title)} className="transition-transform hover:translate-y-[-5px] duration-200">
                    <DocumentTemplate 
                      title={template.title} 
                      type={template.type} 
                      color={template.color}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-16">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-gray-800">Recent documents</h2>
                <div className="flex items-center">
                  <button className="text-sm text-gray-600 hover:text-blue-600 transition-colors flex items-center gap-1 px-3 py-1.5 rounded-md hover:bg-white">
                    Owned by anyone <ChevronDown size={16} />
                  </button>
                </div>
              </div>

              {recentDocuments.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                  {recentDocuments.map((doc) => (
                    <div key={doc.id} className="border rounded-lg p-4 hover:shadow-md cursor-pointer transition-all duration-200 hover:border-blue-300 bg-white">
                      <h3 className="font-medium">{doc.title}</h3>
                      <p className="text-sm text-gray-500">Last edited {doc.lastEdited}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 border rounded-lg bg-white hover:shadow-md transition-all cursor-pointer">
                  <div className="mb-6">
                    <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="font-medium text-gray-700 text-lg mb-2">No documents yet</p>
                  <p className="text-sm text-gray-500 max-w-sm mx-auto">Select a blank document or choose another template above to get started</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <DocumentEditor template={selectedTemplate} onClose={handleCloseEditor} />
      )}
    </div>
  );
};

export default ProducerBuddyDocs;
